// ----------------------------------------------------------------------
// <copyright file="Izenda.ContextMenuStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  var AuthorizationConstants = require('logic/data/Izenda.AuthorizationConstants');
  var LicenseCheckerConstants = require('logic/data/Izenda.LicenseCheckerConstants');
  var localizer = require('share/localization/Izenda.Localizer');
  var uiState = require('logic/Izenda.UiState');

  const { filter, isNil, lowerFirst, forEach, find, placeholder } = require('lodash/fp');

  var menuDefinition = [
    {
      key: 'setupAndLicense',
      icon: 'icon-key',
      forSystemAdmin: true,
      get title() {
        return localizer.getText('CONTEXT_MENU_LABEL_SYSTEM_DB_LICENSE');
      }
    },
    {
      key: 'tenantManagement',
      icon: 'icon-group',
      forSystemUser: true,
      forMultipleTenant: true,
      get title() {
        return localizer.getText('CONTEXT_MENU_LABEL_TENANT_SETUP');
      },
      permissionKeys: [
        AuthorizationConstants.PERMISSION_KEY.TenantSetupActionsCreate,
        AuthorizationConstants.PERMISSION_KEY.TenantSetupActionsEdit,
        AuthorizationConstants.PERMISSION_KEY.TenantSetupActionsDel,
        AuthorizationConstants.PERMISSION_KEY.TenantSetupPermissionsValue
      ],
      permissionOperator: AuthorizationConstants.PERMISSION_OPERATOR.Or
    },
    {
      key: 'dataSetup',
      icon: 'icon-datasetup',
      get title() {
        return localizer.getText('CONTEXT_MENU_LABEL_DATA_SETUP');
      },
      isDirty: false,
      permissionKeys: [
        AuthorizationConstants.PERMISSION_KEY.DataSetupDataModelValue,
        AuthorizationConstants.PERMISSION_KEY.DataSetupAdvancedSettingsCategory,
        AuthorizationConstants.PERMISSION_KEY.DataSetupAdvancedSettingsOthers
      ],
      permissionOperator: AuthorizationConstants.PERMISSION_OPERATOR.Or,
      items: [
        {
          key: 'connectionString',
          icon: 'icon-connectionstring',
          get title() {
            return localizer.getText('CONTEXT_MENU_LABEL_CONNECTION_STRING');
          },
          isDirty: false,
          forSystemAdmin: true
        },
        {
          key: 'dataModel',
          icon: 'icon-datamodel',
          get title() {
            return localizer.getText('CONTEXT_MENU_LABEL_DATA_MODEL');
          },
          isDirty: false,
          needConnectionString: true,
          isDisabled: false,
          permissionKeys: [AuthorizationConstants.PERMISSION_KEY.DataSetupDataModelValue]
        },
        {
          key: 'advanceSettings',
          icon: 'icon-advancesetting',
          get title() {
            return localizer.getText('CONTEXT_MENU_LABEL_ADVANCED_SETTINGS');
          },
          isDisabled: false,
          permissionKeys: function() {
            return function() {
              // Check user is a system user.
              if (uiState.currentUser.systemAdmin) return true;

              // check others
              return this.checkPrivileges(
                [
                  AuthorizationConstants.PERMISSION_KEY.DataSetupAdvancedSettingsCategory,
                  AuthorizationConstants.PERMISSION_KEY.DataSetupAdvancedSettingsOthers
                ],
                AuthorizationConstants.PERMISSION_OPERATOR.Or
              );
            };
          }
        },
        {
          key: 'copyManagement',
          icon: 'icon-copy-mng',
          get title() {
            return localizer.getText('CONTEXT_MENU_LABEL_COPY_MANAGEMENT');
          },
          isDisabled: false,
          forSystemAdmin: true,
          forMultipleTenant: true
        },
        {
          key: 'databaseMapping',
          icon: 'icon-mapping',
          get title() {
            return localizer.getText('CONTEXT_MENU_LABEL_DATABASE_MAPPING');
          },
          isDisabled: false,
          forSystemAdmin: true,
          forMultipleTenant: true
        },
        {
          key: 'import',
          icon: 'icon-import',
          get title() {
            return localizer.getText('CONTEXT_MENU_LABEL_IMPORT');
          },
          isDisable: false,
          forSystemAdmin: true
        },
        {
          key: 'importHistory',
          icon: 'icon-import-history',
          get title() {
            return localizer.getText('CONTEXT_MENU_LABEL_IMPORT_HISTORY');
          },
          isDisable: false,
          forSystemAdmin: true
        }
      ]
    },
    {
      key: 'roleSetup',
      icon: 'icon-group',
      get title() {
        return localizer.getText('CONTEXT_MENU_LABEL_ROLE_SETUP');
      },
      permissionKeys: [
        AuthorizationConstants.PERMISSION_KEY.RoleSetupActionsCreate,
        AuthorizationConstants.PERMISSION_KEY.RoleSetupActionsEdit,
        AuthorizationConstants.PERMISSION_KEY.RoleSetupActionsDel,
        AuthorizationConstants.PERMISSION_KEY.RoleSetupDataModelAccessValue,
        AuthorizationConstants.PERMISSION_KEY.RoleSetupPermissionsValue,
        AuthorizationConstants.PERMISSION_KEY.RoleSetupGrantRoleWithFullReportAndDashboardAccessValue
      ],
      permissionOperator: AuthorizationConstants.PERMISSION_OPERATOR.Or
    },
    {
      key: 'userSetup',
      icon: 'icon-useradmin',
      get title() {
        return localizer.getText('CONTEXT_MENU_LABEL_USER_SETUP');
      },
      permissionKeys: [
        AuthorizationConstants.PERMISSION_KEY.UserSetupActionsCreate,
        AuthorizationConstants.PERMISSION_KEY.UserSetupActionsEdit,
        AuthorizationConstants.PERMISSION_KEY.UserSetupActionsDel,
        AuthorizationConstants.PERMISSION_KEY.UserSetupActionsConfigureSecurityOptions,
        AuthorizationConstants.PERMISSION_KEY.UserSetupUserRoleAssociationValue
      ],
      permissionOperator: AuthorizationConstants.PERMISSION_OPERATOR.Or
    },
    {
      key: 'systemConfig',
      icon: 'icon-systemconfig',
      get title() {
        return localizer.getText('CONTEXT_MENU_LABEL_SYSTEM_CONFIGURATION');
      },
      permissionKeys: [AuthorizationConstants.PERMISSION_KEY.SystemConfigurationScheduledInstancesValue],
      items: [
        {
          key: 'securityPolicies',
          icon: 'icon-lock',
          get title() {
            return localizer.getText('CONTEXT_MENU_LABEL_SECURITY_POLICIES');
          },
          forSystemAdmin: true
        },
        {
          key: 'emailConfiguration',
          icon: 'icon-mail',
          get title() {
            return localizer.getText('CONTEXT_MENU_LABEL_EMAIL');
          },
          forSystemAdmin: true
        },
        {
          key: 'reportConfiguration',
          icon: 'icon-report',
          get title() {
            return localizer.getText('CONTEXT_MENU_LABEL_REPORT');
          },
          forSystemAdmin: true
        },
        {
          key: 'scheduling',
          icon: 'icon-calendar',
          get title() {
            return localizer.getText('CONTEXT_MENU_LABEL_SCHEDULING');
          },
          permissionKeys: [AuthorizationConstants.PERMISSION_KEY.SystemConfigurationScheduledInstancesValue],
          licenseKey: LicenseCheckerConstants.LICENSE_KEY.Scheduling
        }
      ]
    }
  ];

  var extendObservable = require('mobx').extendObservable;
  var action = require('mobx').action;
  var fetcher = require('fetchers/Izenda.SystemSettingFetcher.js');

  function ContextMenuStore() {
    extendObservable(this, {
      contextMenuActiveLink: 'setupAndLicense',
      activeLink: null,
      menuStatus: true,
      contextMenu: menuDefinition,
      flattenContextMenu: function() {
        return placeholder(this.contextMenu)
          .flatMap(function(item) {
            var tempArray = isNil(item.items) ? [] : item.items.toJSON();
            tempArray.push(item);
            return tempArray;
          })
          .flattenDeep();
      },
      isCurrentActiveLinkDisable: function() {
        return !!this.flattenContextMenu.findLast({
          key: this.contextMenuActiveLink
        }).isDisabled;
      }
    });
  }

  ContextMenuStore.prototype.changeActiveLink = action(function(newLink) {
    this.contextMenuActiveLink = this.activeLink = newLink;
  });

  /**
   * load system indicator for detecting changes
   */
  ContextMenuStore.prototype.loadSystemIndicator = function() {
    var updateMenuDefinition = function(result) {
      var dataSetupMenu = find(
        {
          key: 'dataSetup'
        },
        this.contextMenu
      );
      var DIRTY_STATUS = '2';

      forEach(function(item) {
        item.isDirty = false;
      }, dataSetupMenu.items);

      dataSetupMenu.isDirty = false;

      if (
        !isNil(
          find(
            {
              value: DIRTY_STATUS
            },
            result
          )
        )
      ) {
        dataSetupMenu.isDirty = true;
        filter(
          {
            value: DIRTY_STATUS
          },
          result
        ).forEach(function(item) {
          var dbKey = lowerFirst(item.key);

          find(
            {
              key: dbKey
            },
            dataSetupMenu.items
          ).isDirty = true;
        });
      }
    };

    return fetcher.getSystemIndicator(uiState.getTenantId()).then(updateMenuDefinition.bind(this));
  };

  /**
   * disabled some for setting menu item if visible connetion == 0
   * @return {Promise} for unit test to make sure testing after complete set
   */
  ContextMenuStore.prototype.disabledLinkBasedOnConnectionString = function() {
    var needUpdateList = this.flattenContextMenu.filter('needConnectionString');

    var updateMenuDefinition = function(result) {
      needUpdateList.forEach(function(item) {
        item.isDisabled = result === 0;
      });
      return result;
    };

    var resetMenuDefinition = function() {
      needUpdateList.forEach(function(item) {
        item.isDisabled = true;
      });

      return 0;
    };

    return fetcher
      .getNumberOfConnection(uiState.getTenantId())
      .then(updateMenuDefinition.bind(this), resetMenuDefinition.bind(this));
  };

  /**
   * Get Context Menu Store
   */
  (ContextMenuStore.prototype.getHelpSystemUrl = function() {
    return fetcher.getHelpSystemUrl();
  }),
    (module.exports = ContextMenuStore);
})();



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ContextMenuStore.js